/**
 * TanStack Table 전용 컬럼 프리셋
 * - AG Grid의 agc와 동일한 "기능"(텍스트/숫자/날짜/콤보, 읽기전용·편집)을 TanStack 컬럼 형식으로 제공
 * - 사용처: columns 배열을 만들 때 스프레드 후 id, accessorKey, header, size, meta 등만 덮어쓰면 됨
 */

/** 공통 메타 기본값 */
const defaultMeta = (overrides = {}) => ({
  editable: true,
  align: 'left',
  skipPaste: false, // true 이면 붙여넣기 시 해당 컬럼 제외
  ...overrides,
});

/**
 * 읽기 전용 텍스트 컬럼
 * @param {Object} opts - { header, field, size, required, headerAlign, align, sortable, enableSorting, mergeRows, mergeGroupKey, showSearch, onSearch, searchTitle }
 * @param {string} [opts.align] - 데이터(셀) 정렬: 'left' | 'center' | 'right' (미지정 시 기본 left)
 * @param {boolean} [opts.sortable=false] - false 시 헤더 클릭 정렬 비활성화
 * @param {boolean} [opts.mergeRows] - 연속 동일 값 세로 병합 (조회 그리드 권장)
 * @param {string} [opts.mergeGroupKey] - 병합 그룹 키(예: requestUid). 그룹이 바뀌면 병합 끊김
 * @param {boolean} [opts.showSearch] - 셀 안쪽 ··· 코드조회 (명칭열 조회 등)
 */
export function roTextColumn(opts = {}) {
  const {
    header = '',
    field = '',
    size = 150,
    minSize = 50,
    maxSize,
    required = false,
    headerAlign,
    align,
    sortable,
    enableSorting,
    footer,
    visible,
    mergeRows,
    mergeGroupKey,
    showSearch = false,
    onSearch,
    searchTitle = '코드 조회',
  } = opts;
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize,
    maxSize,
    meta: defaultMeta({
      editable: false,
      align: align ?? 'left',
      required,
      ...(headerAlign != null && { headerAlign }),
      ...(visible !== undefined && { visible }),
      ...(mergeRows ? { mergeRows: true } : {}),
      ...(mergeGroupKey != null && mergeGroupKey !== '' ? { mergeGroupKey } : {}),
      ...(showSearch ? { showSearch: true, onSearch, searchTitle } : {}),
    }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
    ...(footer != null && { footer }),
  };
}

/**
 * 편집 가능 텍스트 컬럼
 * @param {Object} opts - { header, field, size, required, skipPaste, headerAlign, align }
 * @param {boolean} opts.skipPaste - true 시 붙여넣기 제외
 * @param {string} [opts.align] - 데이터(셀) 정렬: 'left' | 'center' | 'right' (미지정 시 기본 left)
 */
export function edTextColumn(opts = {}) {
  const {
    header = '',
    field = '',
    size = 150,
    required = false,
    skipPaste = false,
    headerAlign,
    align,
    sortable,
    enableSorting,
    footer,
    visible,
    showSearch = false,
    onSearch,
    searchTitle = '코드 조회',
    editable = true,
    formatCalcBasis = false,
    /** CodeSuggestConfig — LIKE 제안 (docs/CODE_SUGGEST_UX_SPEC.md) */
    suggest = null,
  } = opts;
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize: 50,
    maxSize: undefined,
    meta: defaultMeta({
      editable,
      align: align ?? 'left',
      required,
      type: 'text',
      skipPaste,
      ...(headerAlign != null && { headerAlign }),
      ...(visible !== undefined && { visible }),
      ...(showSearch ? { showSearch: true, onSearch, searchTitle } : {}),
      ...(formatCalcBasis ? { formatCalcBasis: true } : {}),
      ...(suggest ? { suggest } : {}),
    }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
    ...(footer != null && { footer }),
  };
}

/**
 * 데이터키 등: 기본은 읽기 전용, 추가 행(mod === 'A')일 때만 편집 가능한 텍스트 컬럼
 * @param {Object} opts - roTextColumn/edTextColumn과 동일 (header, field, size, required, ...)
 */
export function edTextColumnForKey(opts = {}) {
  const { header = '', field = '', size = 150, required = false, skipPaste = false, headerAlign, align, sortable, enableSorting, footer } = opts;
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize: 50,
    maxSize: undefined,
    meta: defaultMeta({
      editable: (row) => row?.mod === 'A',
      align: align ?? 'left',
      required,
      type: 'text',
      skipPaste,
      ...(headerAlign != null && { headerAlign }),
    }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
    ...(footer != null && { footer }),
  };
}

/**
 * 편집 가능 숫자 컬럼 (우측 정렬, 천단위 포맷)
 * @param {boolean} opts.skipPaste - true 시 붙여넣기 제외
 * @param {string} [opts.align] - 데이터(셀) 정렬: 'left' | 'center' | 'right' (미지정 시 기본 right)
 */
export function edNumberColumn(opts = {}) {
  const {
    header = '',
    field = '',
    size = 150,
    required = false,
    skipPaste = false,
    headerAlign,
    align,
    sortable,
    enableSorting,
    footer,
    editable = true,
  } = opts;
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize: 50,
    maxSize: undefined,
    meta: defaultMeta({
      editable,
      align: align ?? 'right',
      required,
      type: 'number',
      skipPaste,
      ...(headerAlign != null && { headerAlign }),
    }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
    ...(footer != null && { footer }),
  };
}

/**
 * 읽기 전용 숫자 컬럼
 * @param {string} [opts.align] - 데이터(셀) 정렬: 'left' | 'center' | 'right' (미지정 시 기본 right)
 */
export function roNumberColumn(opts = {}) {
  const {
    header = '',
    field = '',
    size = 150,
    headerAlign,
    align,
    sortable,
    enableSorting,
    footer,
    mergeRows,
    mergeGroupKey,
  } = opts;
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize: 50,
    maxSize: undefined,
    meta: defaultMeta({
      editable: false,
      align: align ?? 'right',
      type: 'number',
      ...(headerAlign != null && { headerAlign }),
      ...(mergeRows ? { mergeRows: true } : {}),
      ...(mergeGroupKey != null && mergeGroupKey !== '' ? { mergeGroupKey } : {}),
    }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
    ...(footer != null && { footer }),
  };
}

/**
 * 숫자 2단 스택 컬럼 (상·하 필드 한 열에 표시)
 * - 헤더·셀에 가로 구분선
 * - 클릭 위치(상/하)에 따라 해당 필드 편집
 * @param {Object} opts
 * @param {string} opts.topField
 * @param {string} opts.bottomField
 * @param {string} opts.topLabel
 * @param {string} opts.bottomLabel
 * @param {boolean} [opts.topEditable=true]
 * @param {boolean} [opts.bottomEditable=true]
 * @param {boolean|Function} [opts.editable=true] - false면 상·하 모두 읽기전용
 */
export function stackNumberColumn(opts = {}) {
  const {
    topField = '',
    bottomField = '',
    topLabel = '',
    bottomLabel = '',
    size = 86,
    topEditable = true,
    bottomEditable = true,
    editable = true,
    headerAlign,
    align,
    sortable,
    enableSorting,
    footer,
  } = opts;
  const id = opts.id || `${topField}_${bottomField}`;
  const anyEditable = editable !== false && (topEditable || bottomEditable);
  return {
    id,
    accessorKey: topField,
    header: `${topLabel}/${bottomLabel}`,
    size,
    minSize: 50,
    maxSize: undefined,
    meta: defaultMeta({
      editable: anyEditable ? editable : false,
      align: align ?? 'right',
      type: 'number',
      stackPair: {
        top: topField,
        bottom: bottomField,
        topLabel,
        bottomLabel,
        topEditable,
        bottomEditable,
      },
      ...(headerAlign != null && { headerAlign }),
    }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
    ...(footer != null && { footer }),
  };
}

/**
 * 편집 가능 날짜 컬럼 (DateCalendarPopover)
 * @param {Object} opts - { header, field, size, required, format, min, max, skipPaste, headerAlign, align, editable, showPresets, presetSet }
 * @param {string} opts.format - 표시 형식 (기본 'yyyy-MM-dd'). 예: 'yyyy-MM-dd', 'yyyy/MM/dd', 'yyyyMMdd'
 * @param {string|Date} opts.min - input min (날짜)
 * @param {string|Date} opts.max - input max (날짜)
 * @param {boolean} opts.skipPaste - true 시 붙여넣기 제외
 * @param {boolean} [opts.showPresets=false] - 캘린더 프리셋 (단일 기준일은 기본 숨김, 기간 조회만 true)
 * @param {string} [opts.presetSet='minimal'] - 프리셋 세트
 * @param {string} [opts.align] - 데이터(셀) 정렬: 'left' | 'center' | 'right' (미지정 시 기본 left)
 */
export function edDateColumn(opts = {}) {
  const {
    header = '',
    field = '',
    size = 130,
    required = false,
    format = 'yyyy-MM-dd',
    min,
    max,
    skipPaste = false,
    headerAlign,
    align,
    sortable,
    enableSorting,
    footer,
    editable = true,
    showPresets = false,
    presetSet = 'minimal',
    yearPreset = 'calendar',
    fiscalYear,
    anchorFrom,
  } = opts;
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize: 80,
    maxSize: undefined,
    meta: defaultMeta({
      editable,
      align: align ?? 'left',
      required,
      type: 'date',
      format,
      min,
      max,
      skipPaste,
      showPresets,
      presetSet,
      yearPreset,
      fiscalYear,
      anchorFrom,
      ...(headerAlign != null && { headerAlign }),
    }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
    ...(footer != null && { footer }),
  };
}

/**
 * 편집 가능 콤보(셀렉트) 컬럼
 * @param {Object} opts - { header, field, size, required, options, skipPaste, headerAlign, align }
 * @param {Array} opts.options - [{ value, label }] 또는 [value, ...]
 * @param {boolean} opts.skipPaste - true 시 붙여넣기 제외
 * @param {string} [opts.align] - 데이터(셀) 정렬: 'left' | 'center' | 'right' (미지정 시 기본 left)
 */
export function edComboColumn(opts = {}) {
  const { header = '', field = '', size = 150, required = false, options = [], skipPaste = false, headerAlign, align, sortable, enableSorting, footer } = opts;
  const values = Array.isArray(options)
    ? options.map((o) => (typeof o === 'object' && o != null && 'value' in o ? o : { value: o, label: String(o) }))
    : [];
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize: 50,
    maxSize: undefined,
    meta: defaultMeta({ editable: true, align: align ?? 'center', required, type: 'combo', options: values, skipPaste, ...(headerAlign != null && { headerAlign }) }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
    ...(footer != null && { footer }),
  };
}

/**
 * 편집 가능 체크박스 컬럼 (값: 'Y'/'N', RealGrid gEdCheckboxCol 호환)
 * @param {Object} opts - { header, field, size, required, headerAlign, align, trueValue, falseValue }
 * @param {string} [opts.trueValue] - 체크 시 저장값 (기본 'Y')
 * @param {string} [opts.falseValue] - 해제 시 저장값 (기본 'N')
 */
export function edCheckboxColumn(opts = {}) {
  const {
    header = '',
    field = '',
    size = 100,
    required = false,
    headerAlign,
    align,
    trueValue,
    falseValue,
    sortable,
    enableSorting,
    editable = true,
    title,
  } = opts;
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize: 50,
    maxSize: undefined,
    meta: defaultMeta({
      editable,
      align: align ?? 'center',
      required,
      type: 'checkbox',
      skipPaste: true,
      trueValue: trueValue ?? 'Y',
      falseValue: falseValue ?? 'N',
      ...(headerAlign != null && { headerAlign }),
      ...(title != null && title !== '' ? { title } : {}),
    }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
  };
}

/**
 * 읽기 전용 체크박스 컬럼 (값: 'Y'/'N')
 */
export function roCheckboxColumn(opts = {}) {
  const { header = '', field = '', size = 100, headerAlign, align, trueValue, falseValue, sortable, enableSorting } = opts;
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize: 50,
    maxSize: undefined,
    meta: defaultMeta({
      editable: false,
      align: align ?? 'center',
      type: 'checkbox',
      trueValue: trueValue ?? 'Y',
      falseValue: falseValue ?? 'N',
      ...(headerAlign != null && { headerAlign }),
    }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
  };
}

/**
 * 읽기 전용 이미지/아이콘 컬럼 (클릭 시 팝업 등 액션용)
 * @param {Object} opts - { header, field, size, src, alt, headerAlign, align }
 * @param {string} [opts.src] - 이미지 URL (미지정 시 기본 돋보기 아이콘 사용)
 * @param {string} [opts.alt] - img alt 텍스트
 * @param {string} [opts.align] - 셀 정렬 (기본 'center')
 */
export function roImageColumn(opts = {}) {
  const { header = '', field = '', size = 50, src, alt = '', headerAlign, align, sortable, enableSorting } = opts;
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize: 40,
    maxSize: undefined,
    meta: defaultMeta({
      editable: false,
      align: align ?? 'center',
      type: 'img',
      imgSrc: src,
      imgAlt: alt,
      ...(headerAlign != null && { headerAlign }),
    }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
  };
}

/**
 * 트리 그리드용 노드 컬럼 (들여쓰기 + 펼침 버튼 + 값). treeOptions 지정 시에만 트리 셀 렌더.
 * @param {Object} opts - { header, field, size, headerAlign, align }
 */
export function roTreeColumn(opts = {}) {
  const { header = '', field = '', size = 200, headerAlign, align, sortable, enableSorting } = opts;
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize: 80,
    maxSize: undefined,
    meta: defaultMeta({ editable: false, align: align ?? 'left', treeColumn: true, ...(headerAlign != null && { headerAlign }) }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
  };
}

/**
 * 트리 그리드용 편집 가능 노드 컬럼 (들여쓰기 + 펼침 + 값 입력)
 * - roTreeColumn과 동일하게 meta.treeColumn 유지, editable: true 로 직접 수정 가능
 */
export function edTreeColumn(opts = {}) {
  const {
    header = '',
    field = '',
    size = 200,
    required = false,
    skipPaste = false,
    headerAlign,
    align,
    sortable,
    enableSorting,
  } = opts;
  return {
    id: field,
    accessorKey: field,
    header,
    size,
    minSize: 80,
    maxSize: undefined,
    meta: defaultMeta({
      editable: true,
      align: align ?? 'left',
      treeColumn: true,
      type: 'text',
      required,
      skipPaste,
      ...(headerAlign != null && { headerAlign }),
    }),
    ...(sortable !== undefined && { sortable }),
    ...(enableSorting !== undefined && { enableSorting }),
  };
}

export const hiCol = {
  roTextColumn,
  edTextColumn,
  edTextColumnForKey,
  edNumberColumn,
  roNumberColumn,
  stackNumberColumn,
  edDateColumn,
  edComboColumn,
  edCheckboxColumn,
  roCheckboxColumn,
  roImageColumn,
  roTreeColumn,
  edTreeColumn,
};
